import React, { useState, useEffect } from 'react';
import LandingPage from './components/LandingPage';
import QuestionnairePage from './components/QuestionnairePage';
import ReportPage from './components/ReportPage';
import { loadQuestions, saveResponse, checkExistingResponse, checkRateLimit } from './utils/dataHandler';

function App() {
  const [currentPage, setCurrentPage] = useState('landing');
  const [userInfo, setUserInfo] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingResume, setPendingResume] = useState(null); // Store resume data until questions load

  useEffect(() => {
    // Load questions on app start
    const loadQuestionsData = async () => {
      try {
        console.log('App: Starting to load questions...');
        const questionsData = await loadQuestions();
        console.log('App: Questions loaded successfully:', questionsData.length);
        setQuestions(questionsData);
        console.log('Categories found:', [...new Set(questionsData.map(q => q.category))]);
      } catch (err) {
        console.error('App: Error loading questions:', err);
        setError(`Failed to load questionnaire data: ${err.message}`);
      }
    };
    loadQuestionsData();
  }, []);

  // Handle resume logic when questions are loaded
  useEffect(() => {
    if (questions.length > 0 && pendingResume) {
      const { loadedResponses } = pendingResume;
      
      // Find the first unanswered question to resume from
      const firstUnansweredIndex = questions.findIndex(q => {
        const questionKey = `${q.category}|||${q.question}`;
        return loadedResponses[questionKey] === undefined;
      });
      if (firstUnansweredIndex !== -1) {
        setCurrentQuestionIndex(firstUnansweredIndex);
        console.log('Resuming from question index:', firstUnansweredIndex);
      } else {
        // All questions answered, go to last question
        setCurrentQuestionIndex(questions.length - 1);
        console.log('All questions answered, going to last question');
      }
      
      setPendingResume(null); // Clear pending resume data
    }
  }, [questions, pendingResume]);

  const handleStart = async (formData) => {
    setLoading(true);
    setError('');
    
    try {
      // First, check rate limits before allowing assessment to start
      const rateLimitCheck = await checkRateLimit();

      if (!rateLimitCheck.allowed) {
        // Show user-friendly rate limit message
        setError(rateLimitCheck.message || 'Assessment is temporarily unavailable. Please contact info@thedewpointgroup.com for assistance.');
        setLoading(false);
        return;
      }

      // Rate limit check passed, now check if user has existing responses
      let existingResponse;
      try {
        existingResponse = await checkExistingResponse(formData.userName, formData.companyName);
      } catch (error) {
        // Handle rate limit errors from checkExistingResponse
        setError(error.message);
        setLoading(false);
        return;
      }
      
      if (existingResponse && existingResponse.isCompleted) {
        setError('Assessment is already completed, Thank you');
        setLoading(false);
        return;
      }

      // Set user info
      setUserInfo(formData);
      
      // Load existing partial responses if they exist
      if (existingResponse && existingResponse.responses) {
        const loadedResponses = {};
        existingResponse.responses.forEach(response => {
          if (response.employeeResponse !== null && response.employeeResponse !== undefined) {
            // Create a unique key using category + question text to match responses
            const questionKey = `${response.category}|||${response.question}`;
            loadedResponses[questionKey] = response.employeeResponse;
          }
        });
        setResponses(loadedResponses);
        
        console.log('Loaded existing responses:', loadedResponses);
        
        // If questions are already loaded, find resume point immediately
        if (questions.length > 0) {
          const firstUnansweredIndex = questions.findIndex(q => {
            const questionKey = `${q.category}|||${q.question}`;
            return loadedResponses[questionKey] === undefined;
          });
          if (firstUnansweredIndex !== -1) {
            setCurrentQuestionIndex(firstUnansweredIndex);
            console.log('Resuming from question index:', firstUnansweredIndex);
          } else {
            setCurrentQuestionIndex(questions.length - 1);
          }
        } else {
          // Store resume data for when questions load
          setPendingResume({ loadedResponses });
        }
      }
      
      setCurrentPage('questionnaire');
    } catch (err) {
      console.error('Start error:', err);
      setError('Failed to start assessment. Please try again or contact info@thedewpointgroup.com');
    }
    
    setLoading(false);
  };

  const handleQuestionResponse = (question, response) => {
    // Create a unique key using category + question text
    const questionKey = `${question.category}|||${question.question}`;
    setResponses(prev => ({
      ...prev,
      [questionKey]: response
    }));
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      // All questions completed, save responses and show completion
      handleCompleteQuestionnaire();
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleCompleteQuestionnaire = async () => {
    setLoading(true);
    try {
      await saveResponse(userInfo, questions, responses, false); // Final save
      setCurrentPage('completion');
    } catch (err) {
      console.error('Error saving responses:', err);
      setError('Failed to save responses. Please try again.');
    }
    setLoading(false);
  };

  const handleSaveAndContinue = async () => {
    setLoading(true);
    try {
      await saveResponse(userInfo, questions, responses, true); // Partial save
      setError(''); // Clear any previous errors
      
      // Show prominent success message briefly and return immediately
      const successMessage = 'Your answers are saved';
      setError(successMessage);
      
      // Return to landing page immediately
      setTimeout(() => {
        setError('');
        handleReturnToLanding();
      }, 500); // Very brief delay just to show the message
      
    } catch (err) {
      console.error('Error saving partial responses:', err);
      setError('Failed to save progress. Please try again.');
    }
    setLoading(false);
  };

  const handleReturnToLanding = () => {
    setCurrentPage('landing');
    setUserInfo(null);
    setCurrentQuestionIndex(0);
    setResponses({});
    setError('');
    setPendingResume(null);
  };

  const getCompletedCategories = () => {
    const categories = [...new Set(questions.map(q => q.category))];
    return categories.filter(category => {
      const categoryQuestions = questions.filter(q => q.category === category);
      return categoryQuestions.every(q => {
        const questionKey = `${q.category}|||${q.question}`;
        return responses[questionKey] !== undefined;
      });
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100">
      {error && (
        <div className={`fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 px-8 py-6 rounded-lg shadow-2xl z-50 text-center ${
          error.includes('Your answers are saved') 
            ? 'bg-green-600 text-white text-xl font-semibold' 
            : error.includes('successfully') || error.includes('saved') 
            ? 'bg-green-500 text-white' 
            : 'bg-red-500 text-white'
        }`}>
          {error}
        </div>
      )}
      
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Processing...</p>
          </div>
        </div>
      )}

      {currentPage === 'landing' && (
        <LandingPage 
          onStart={handleStart}
          loading={loading}
          error={error}
        />
      )}

      {currentPage === 'questionnaire' && questions.length > 0 && (
        <QuestionnairePage
          question={questions[currentQuestionIndex]}
          currentIndex={currentQuestionIndex}
          totalQuestions={questions.length}
          response={responses[`${questions[currentQuestionIndex]?.category}|||${questions[currentQuestionIndex]?.question}`]}
          onResponse={handleQuestionResponse}
          onNext={handleNextQuestion}
          onPrevious={handlePreviousQuestion}
          onSaveAndContinue={handleSaveAndContinue}
          completedCategories={getCompletedCategories()}
          allCategories={[...new Set(questions.map(q => q.category))]}
          userInfo={userInfo}
          loading={loading}
        />
      )}

      {currentPage === 'completion' && (
        <ReportPage 
          userInfo={userInfo}
          onReturnToLanding={handleReturnToLanding}
        />
      )}
    </div>
  );
}

export default App;